import * as psl from 'psl';
import { useMemo } from 'react';

import { useMy } from '@/hooks';
import { useGetFile } from '@/hooks/useFiles';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { Access, Attribute, ResourceType, Seed } from '@/types';
import { useMergeStatus } from '@/utils/api';
import { getAccessEmail } from '@/utils/getAccessEmail';

/**
 * Coordinated seed drawer data fetching hook
 *
 * KEY-BASED PATTERN: Fetches seed data internally using seedKey, eliminating
 * race conditions from dual state sources. The URL param (seedDrawerKey) is
 * the single source of truth.
 *
 * Manages parallel data fetching for the SeedDrawer with unified state:
 * - Seed data (fetched by key - single source of truth)
 * - Attributes for the seed (source information, discovery metadata)
 * - WHOIS details for domain/IP display
 * - WHOIS history for historical tracking
 * - Access information for user lookups
 *
 * Performance strategy:
 * - All queries run in parallel via React Query's automatic batching
 * - Loading states unified to prevent UI flicker during drawer transitions
 * - Error states consolidated for consistent error handling
 * - Queries disabled when seedKey is empty (drawer closed)
 * - Refetch coordinated across all queries
 *
 * Data transformations:
 * - Computes origination information from seed source and attributes
 * - Formats WHOIS details for display (string or JSON formatting)
 * - Extracts domain from seed identifier for file lookups
 *
 * @param seedKey - The seed key to fetch data for (from URL param seedDrawerKey)
 * @returns Coordinated data, loading states, errors, and refetch utilities
 *
 * @example
 * ```tsx
 * const {
 *   seed,
 *   attributes,
 *   displayDetails,
 *   history,
 *   accesss,
 *   originationInfo,
 *   isInitialLoading,
 *   error,
 *   refetch
 * } = useSeedDrawerData(seedDrawerKey);
 *
 * // Use unified loading state
 * if (isInitialLoading) return <Loader />;
 *
 * // Use data for drawer content
 * <SeedDrawerContent
 *   seed={seed}
 *   displayDetails={displayDetails}
 *   history={history}
 *   originationInfo={originationInfo}
 * />
 * ```
 */
export type WhoisHistoryStatus = 'covered' | 'empty' | 'error';

export interface ParsedHistory {
  status: WhoisHistoryStatus | undefined;
  records: string[] | undefined;
}

export function parseWhoisHistoryEnvelope(raw: unknown): ParsedHistory {
  if (!raw) return { status: undefined, records: undefined };

  if (typeof raw === 'object' && raw !== null && 'status' in raw) {
    const envelope = raw as { status: string; records?: string[] };
    return {
      status: envelope.status as WhoisHistoryStatus,
      records: envelope.records,
    };
  }

  // Legacy format: raw array of records (before envelope migration)
  if (Array.isArray(raw)) {
    return { status: 'covered', records: raw as string[] };
  }

  return { status: undefined, records: undefined };
}

export const useSeedDrawerData = (seedKey: string) => {
  // Query 1: Fetch main seed data by key (single source of truth)
  const seedQuery = useMy(
    {
      query: {
        node: {
          labels: ['Seed'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: seedKey || '',
            },
          ],
        },
      },
      resource: ResourceType.Seed,
    },
    {
      enabled: Boolean(seedKey),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );
  const seedData = seedQuery.data ?? [];
  const seedStatus = seedQuery.status;

  // Extract seed from response
  const seed = seedData[0] || null;

  // Extract domain for file lookups (WHOIS data is stored by domain)
  const domain = seed ? psl.get(seed.identifier) : null;

  // 1. Fetch attributes for the seed (source information, discovery metadata)
  const attributesQuery = useMy(
    {
      resource: ResourceType.Attribute,
      query: {
        node: {
          labels: ['Attribute'],
          filters: [
            {
              field: 'source',
              operator: '=',
              value: `#asset#${seed?.identifier}`,
            },
          ],
        },
      },
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 2. Fetch WHOIS details (for details tab display)
  const detailsQuery = useGetFile(
    {
      name: `whois/${domain || seed?.identifier}`,
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 3. Fetch WHOIS history (for history tab display)
  const historyQuery = useGetFile(
    {
      name: `whois-history/${domain || seed?.identifier}`,
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 4. Fetch access information (for user email lookups)
  const accessQuery = useGenericSearch(
    {
      query: '#access',
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 5. Extract access array from generic search response
  const accesss = useMemo(
    () => accessQuery.data?.accesss || [],
    [accessQuery.data?.accesss]
  );

  // 6. Format WHOIS details for display
  const displayDetails = useMemo(() => {
    const details = detailsQuery.data;
    return typeof details === 'string'
      ? details
      : JSON.stringify(details, null, 2);
  }, [detailsQuery.data]);

  // 6b. Parse WHOIS history envelope (structured file: {status, records})
  const parsedHistory = useMemo(
    () => parseWhoisHistoryEnvelope(historyQuery.data),
    [historyQuery.data]
  );

  // 7. Compute origination information (manual additions + discovery sources)
  const originationInfo = useMemo(() => {
    const discoveries = new Set<string>();

    if (!seed) return [];

    // Check if manually added (source contains email)
    if (seed.source?.includes('@')) {
      const by = getAccessEmail(accesss, seed.origin);
      discoveries.add(`Added by ${by}`);
    }

    // Check for source attributes (discovery methods)
    attributesQuery.data?.forEach(attr => {
      if (attr.name === 'source' && seed.identifier) {
        // Skip if the source value references the seed itself
        if (attr.value.includes(seed.identifier)) {
          return;
        }

        // Format source info
        if (attr.value === 'provided') {
          discoveries.add('Manually added');
        } else if (!attr.value.startsWith('#')) {
          // For integration discoveries (NS1, Whoxy, etc)
          discoveries.add(`Discovered by ${attr.value}`);
        }
      }
    });

    return Array.from(discoveries);
  }, [seed, attributesQuery.data, accesss]);

  // 8. Merge loading states (unified loading experience)
  // Include seedStatus since seed is now fetched internally
  const isLoading = useMergeStatus(
    seedStatus,
    attributesQuery.status,
    detailsQuery.status,
    historyQuery.status,
    accessQuery.status
  );

  // Initial loading state (seed must be loaded before showing content)
  const isInitialLoading = seedStatus === 'pending';

  // 9. Consolidate error states (first error takes precedence)
  const error =
    attributesQuery.error ||
    detailsQuery.error ||
    historyQuery.error ||
    accessQuery.error ||
    null;

  // 10. Coordinated refetch function
  const refetch = () => {
    seedQuery.refetch();
    attributesQuery.refetch();
    detailsQuery.refetch();
    historyQuery.refetch();
    accessQuery.refetch();
  };

  return {
    // Data - seed is now fetched internally (single source of truth)
    seed,
    attributes: attributesQuery.data,
    displayDetails,
    history: parsedHistory.records,
    whoisHistoryStatus: parsedHistory.status,
    accesss,
    originationInfo,

    // Individual query statuses (for granular loading indicators)
    seedStatus,
    attributesStatus: attributesQuery.status,
    detailsStatus: detailsQuery.status,
    historyStatus: historyQuery.status,
    accessStatus: accessQuery.status,

    // Unified loading states
    isLoading: isLoading === 'pending',
    isInitialLoading,
    isAttributesLoading: attributesQuery.status === 'pending',
    isDetailsLoading: detailsQuery.status === 'pending',
    isHistoryLoading: historyQuery.status === 'pending',
    isAccessLoading: accessQuery.status === 'pending',

    // Error states
    error,
    attributesError: attributesQuery.error || null,
    detailsError: detailsQuery.error || null,
    historyError: historyQuery.error || null,
    accessError: accessQuery.error || null,

    // Utilities
    refetch,
    refetchSeed: seedQuery.refetch,
    refetchAttributes: attributesQuery.refetch,
    refetchDetails: detailsQuery.refetch,
    refetchHistory: historyQuery.refetch,
    refetchAccess: accessQuery.refetch,
  };
};

/**
 * Return type for useSeedDrawerData hook
 */
export interface UseSeedDrawerDataReturn {
  // Data - seed is now fetched internally (single source of truth)
  seed: Seed | null;
  attributes: Attribute[] | undefined;
  displayDetails: string;
  history: string[] | undefined;
  whoisHistoryStatus: WhoisHistoryStatus | undefined;
  accesss: Access[];
  originationInfo: string[];

  // Individual query statuses
  seedStatus: 'pending' | 'error' | 'success';
  attributesStatus: 'pending' | 'error' | 'success';
  detailsStatus: 'pending' | 'error' | 'success';
  historyStatus: 'pending' | 'error' | 'success';
  accessStatus: 'pending' | 'error' | 'success';

  // Unified loading states
  isLoading: boolean;
  isInitialLoading: boolean;
  isAttributesLoading: boolean;
  isDetailsLoading: boolean;
  isHistoryLoading: boolean;
  isAccessLoading: boolean;

  // Error states
  error: Error | null;
  attributesError: Error | null;
  detailsError: Error | null;
  historyError: Error | null;
  accessError: Error | null;

  // Utilities
  refetch: () => void;
  refetchSeed: () => void;
  refetchAttributes: () => void;
  refetchDetails: () => void;
  refetchHistory: () => void;
  refetchAccess: () => void;
}
